import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useIntl } from "react-intl";

import { api } from "@/api/api";
import { useFeatureIsOn } from "@/app/featureFlags";
import type {
  ValidateFlightPromoCodeBodyInputDto,
  ValidateFlightPromoCodeOutputDto,
  ValidateHotelPromoCodeBodyInputDto,
  ValidateHotelPromoCodeOutputDto,
  ValidatePackagePromoCodeBodyInputDto,
  ValidatePackagePromoCodeOutputDto,
} from "@/types/codegen";
import { formatCurrency } from "@/utils/currency";

export type ValidationStatus = {
  type: "success" | "error" | "loading";
  message?: string;
};

type ParamsMap = {
  trips: ValidatePackagePromoCodeBodyInputDto;
  flights: ValidateFlightPromoCodeBodyInputDto;
  hotels: ValidateHotelPromoCodeBodyInputDto;
};

type OutputMap = {
  trips: ValidatePackagePromoCodeOutputDto;
  flights: ValidateFlightPromoCodeOutputDto;
  hotels: ValidateHotelPromoCodeOutputDto;
};

type PromoCodeValidationQueryKey<T extends keyof ParamsMap> = readonly [
  T,
  "promo-code-validation",
  ParamsMap[T],
];

const serviceToUrlMap: Record<keyof ParamsMap, string> = {
  trips: "/packages/promo-codes/validate",
  flights: "/flights/promo-codes/validate",
  hotels: "/hotels/promo-codes/validate",
};

type Params<T extends keyof ParamsMap> = {
  service: T;
  validationRequestData: ParamsMap[T];
  onCodeApplied?: (promoCode: string, data: OutputMap[T]["data"]) => void;
  onCodeFailure?: (promoCode: string, errorMessage: string) => void;
};

export function usePromoCodeValidation<T extends keyof ParamsMap>({
  service,
  validationRequestData,
  onCodeApplied,
  onCodeFailure,
}: Params<T>) {
  const intl = useIntl();

  const arePromoCodesEnabled = useFeatureIsOn("promo-codes");

  const { data, isError, isFetching, refetch } = useQuery<
    AxiosResponse<OutputMap[T]>,
    Error,
    OutputMap[T]["data"],
    PromoCodeValidationQueryKey<T>
  >({
    queryKey: [service, "promo-code-validation", validationRequestData],
    queryFn: async ({ signal, queryKey: [, , payload] }) => {
      try {
        const response = await api.post<OutputMap[T]>(
          serviceToUrlMap[service],
          payload,
          { signal },
        );

        const innerData = response.data.data;
        if (innerData.result === true) {
          onCodeApplied?.(payload.promoCode, innerData);
        } else {
          onCodeFailure?.(
            payload.promoCode,
            innerData.message ?? "Something went wrong",
          );
        }

        return response;
      } catch (err) {
        onCodeFailure?.(
          payload.promoCode,
          err instanceof Error ? err.message : "Something went wrong",
        );

        throw err;
      }
    },
    select: (response) => response.data.data,
    enabled: arePromoCodesEnabled && Boolean(validationRequestData.promoCode),
    refetchOnMount: true,
    staleTime: 0,
  });

  const validationStatus: ValidationStatus | undefined = isFetching
    ? { type: "loading" }
    : isError
      ? {
          type: "error",
          message: intl.formatMessage({
            defaultMessage: "Something went wrong. Please try again.",
            description:
              "Error shown when applying the promo code fails unexpectedly",
          }),
        }
      : data?.result === false
        ? {
            type: "error",
            message:
              data.message ||
              intl.formatMessage({
                defaultMessage:
                  "Invalid promo code. Please check and try again.",
                description:
                  "Error shown when the entered promo code is not valid",
              }),
          }
        : data?.result === true
          ? {
              type: "success",
              message: intl.formatMessage(
                {
                  defaultMessage: "You just saved {amount}!",
                  description:
                    "Success message shown after a promo code is applied",
                },
                { amount: formatCurrency(data.discountAmount) },
              ),
            }
          : undefined;

  return {
    validationStatus,
    data,
    revalidate: refetch,
  };
}
